import React, { Ref, useEffect, useState } from "react";
import moment from "moment";
import { FastField, Field, Form } from "formik";
import { Fieldset, Layout } from "@vwfs-bronson/bronson-react";
import * as Validation from "../../validation";
import { FormSection2 } from "../Sections/FormSection";
import toOptions from "../../functional/toOptions";
import { Output } from "../../Text";
import {
  TextField,
  SelectField,
  FieldDependent,
  Conditional,
  renderRadioGroup,
} from "../../formik";
import { DateField } from "../../formik/DateField";
import { resetField } from "../../formik/resetField";
import { isEuCitizen } from "../../common/countryCheck";
import { Analytics } from '../../services/analytics';
import { trackAbort, trackFirstCustomerInteraction } from '../page.utils';

const select = (whitelist: any[], all: any[]) =>
  all.filter((v) => whitelist.includes(v.key));

const ContactDetailsSectionFull = ({
  closed,
  success,
  showEditButton,
  onIconClick,
  form,
  buttons,
  containerRef,
}: {
  closed: boolean;
  success: boolean;
  showEditButton: boolean;
  onIconClick: (...args: any) => any;
  form: any;
  buttons: any;
  containerRef: Ref<any>;
}) => {
  const [changed, setChanged] = useState(false)
  const [lastTouchedField, setLastTouchedField] = useState('')

  const defaultTrackingData = {
    [Analytics.keys.pageName()]: 'Application form',
    [Analytics.keys.viewChange()]: 'Contact data',
    [Analytics.keys.formType()]: 'Personal data',
    [Analytics.keys.formName()]: 'Contact data'
  }

  const trackPage = () => {
    setTimeout(() => {
      const pageData = {
        [Analytics.keys.eventType(0)]: 'pageView',
        [Analytics.keys.eventAction(0)]: 'Success',
      }
      Analytics.setTrackingData({...defaultTrackingData, ...pageData})
      Analytics.trackPage()
    }, 1000)
  }

  const trimFields = (ev: any) => {
    const fieldsToTrim = ["email", "mobilePhoneNumber"]
    if (fieldsToTrim.includes(ev?.target?.name)) {
      form.setFieldValue(ev?.target?.name, ev?.target?.value?.trim());
    }
  }

  const onBlur = (ev: any) => {
    if (!closed && ev && ev.target && ev.target.name) {
      setLastTouchedField(ev.target.name.split('.')[ev.target.name.split('.').length - 1])
      if (!changed) {
        setChanged(true)
        trackFirstCustomerInteraction(defaultTrackingData)
      }
    }
  }

  const onUnload = () => {
    if (!closed) {
      trackAbort(lastTouchedField, defaultTrackingData)
    }
  }

  const ownUnload = () => {
    window.addEventListener('unload', onUnload)
  }

  const desubscribeUnload = () => {
    window.removeEventListener('unload', onUnload)
  }

  useEffect(() => {
    if (!closed) {
      trackPage()
      ownUnload()
      return () => {
        desubscribeUnload()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closed])
  
  return (
    <FormSection2
      containerRef={containerRef}
      title={Output.resolve("pages.applicationForm.panels.contactData.title")}
      subtitle={Output.resolve(
        "pages.applicationForm.panels.contactData.subtitle"
      )}
      success={success}
      expanded={!closed}
      onHeaderClick={onIconClick}
      edit={
        !showEditButton
          ? undefined
          : {
              label: Output.resolve("pages.applicationForm.buttonEdit"),
              onClick: onIconClick,
            }
      }
    >
      <Form {...form} onChange={trimFields} onBlur={onBlur} floatingLabel>
        <Fieldset>
          <Fieldset.Row>
            <Layout>
              <Layout.Item default="6/12" m="1/2" xs="1/1">
                <FastField
                  name="street"
                  label={Output.resolve(
                    "pages.applicationForm.panels.contactData.fields.street.label"
                  )}
                  maxLength="30"
                  component={TextField}
                />
              </Layout.Item>
              <Layout.Item default="6/12" m="1/2" xs="1/1">
                <FastField
                  name="housenumber"
                  label={Output.resolve(
                    "pages.applicationForm.panels.contactData.fields.houseNumber.label"
                  )}
                  component={TextField}
                />
              </Layout.Item>
            </Layout>
          </Fieldset.Row>
          <Fieldset.Row>
            <Layout>
              <Layout.Item default="6/12" m="1/2" xs="1/1">
                <FastField
                  name="postcode"
                  label={Output.resolve(
                    "pages.heyCar.fullAppForm.contactSection.postCode"
                  )}
                  component={TextField}
                />
              </Layout.Item>
              <Layout.Item default="6/12" m="1/2" xs="1/1">
                <FastField
                  name="city"
                  label={Output.resolve(
                    "pages.applicationForm.panels.contactData.fields.city.label"
                  )}
                  maxLength="30"
                  component={TextField}
                />
              </Layout.Item>
            </Layout>
          </Fieldset.Row>
          <Fieldset.Row>
            <Layout>
              <Layout.Item default="6/12" m="1/2" xs="1/1">
                <FastField
                  name="country"
                  label={Output.resolve(
                    "pages.applicationForm.panels.contactData.fields.country.label"
                  )}
                  component={SelectField}
                >
                  {Output.resolve("generic.countrylist", []).map(
                    (
                      {
                        key: value,
                        value: label,
                      }: { key: string; value: string },
                      index: number
                    ) => (
                      <option key={index} value={value}>
                        {label}
                      </option>
                    )
                  )}
                </FastField>
              </Layout.Item>
              <Layout.Item default="6/12" m="1/2" xs="1/1">
                <FastField
                  name="residentSince"
                  label={Output.resolve(
                    "pages.applicationForm.panels.contactData.fields.residentSince.label"
                  )}
                  component={DateField}
                />
              </Layout.Item>
            </Layout>
          </Fieldset.Row>
          <FieldDependent
            names={["residentSince"]}
            render={({
              values: { residentSince },
              formik,
            }: {
              values: any;
              formik: any;
            }) => (
              <Conditional
                collapsed={
                  !residentSince ||
                  !moment(residentSince).isAfter(moment().subtract(3, "years"))
                }
                onCollapse={() => {
                  resetField("prevstreet", formik);
                  resetField("prevhousenumber", formik);
                  resetField("prevpostcode", formik);
                  resetField("prevcity", formik);
                  resetField("prevcountry", formik);
                }}
              >
                <Fieldset>
                  <Fieldset.Row>
                    <Layout>
                      <Layout.Item default="12/12" m="2/2" xs="1/1">
                        <b>
                          {Output.resolve(
                            "pages.applicationForm.panels.contactData.paragraph"
                          )}
                        </b>
                      </Layout.Item>
                    </Layout>
                  </Fieldset.Row>
                  <Fieldset.Row>
                    <Layout>
                      <Layout.Item default="6/12" m="1/2" xs="1/1">
                        <FastField
                          name="prevstreet"
                          label={Output.resolve(
                            "pages.applicationForm.panels.contactData.fields.street.label"
                          )}
                          maxLength="30"
                          component={TextField}
                        />
                      </Layout.Item>
                      <Layout.Item default="6/12" m="1/2" xs="1/1">
                        <FastField
                          name="prevhousenumber"
                          label={Output.resolve(
                            "pages.applicationForm.panels.contactData.fields.houseNumber.label"
                          )}
                          component={TextField}
                        />
                      </Layout.Item>
                    </Layout>
                  </Fieldset.Row>
                  <Fieldset.Row>
                    <Layout>
                      <Layout.Item default="6/12" m="1/2" xs="1/1">
                        <FastField
                          name="prevpostcode"
                          label={Output.resolve(
                            "pages.heyCar.fullAppForm.contactSection.postCodeLong"
                          )}
                          component={TextField}
                        />
                      </Layout.Item>
                      <Layout.Item default="6/12" m="1/2" xs="1/1">
                        <FastField
                          name="prevcity"
                          label={Output.resolve(
                            "pages.applicationForm.panels.contactData.fields.city.label"
                          )}
                          maxLength="30"
                          component={TextField}
                        />
                      </Layout.Item>
                    </Layout>
                  </Fieldset.Row>
                  <Fieldset.Row>
                    <Layout>
                      <Layout.Item default="6/12" m="1/2" xs="1/1">
                        <FastField
                          name="prevcountry"
                          label={Output.resolve(
                            "pages.applicationForm.panels.contactData.fields.country.label"
                          )}
                          component={SelectField}
                        >
                          {Output.resolve("generic.countrylist", []).map(
                            (
                              {
                                key: value,
                                value: label,
                              }: { key: string; value: string },
                              index: number
                            ) => (
                              <option key={index} value={value}>
                                {label}
                              </option>
                            )
                          )}
                        </FastField>
                      </Layout.Item>
                    </Layout>
                  </Fieldset.Row>
                </Fieldset>
              </Conditional>
            )}
          />
          <FieldDependent
            names={["country", "nationality"]}
            render={({
              values: { country, nationality },
              formik,
            }: {
              values: any;
              formik: any;
            }) => (
              <Conditional
                collapsed={isEuCitizen(nationality) || country !== "DE"}
                onCollapse={() => {
                  resetField("isLivingInDeStartDate", formik);
                  resetField("residencePermitStatus", formik);
                  resetField("workPermitStatus", formik);
                  resetField("residencePermitEndDate", formik);
                  resetField("workPermitEndDate", formik);
                }}
              >
                <Fieldset>
                  <Fieldset.Row>
                    <Layout>
                      <Layout.Item default="6/12" m="1/2" xs="1/1">
                        <FastField
                          name="isLivingInDeStartDate"
                          label={Output.resolve(
                            "pages.heyCar.fullAppForm.contactSection.isLivingInDeStartDate"
                          )}
                          component={DateField}
                        />
                      </Layout.Item>
                    </Layout>
                  </Fieldset.Row>
                  <Fieldset.Row>
                    <Layout>
                      <Layout.Item default="6/12" m="1/2" xs="1/1">
                        {renderRadioGroup({
                          fieldName: "residencePermitStatus",
                          tooltip: undefined,
                          legend: Output.resolve(
                            "pages.heyCar.fullAppForm.contactSection.residencePermitStatus.label"
                          ),
                          options: select(
                            ["restricted", "unrestricted"],
                            Output.resolve(
                              "pages.heyCar.fullAppForm.contactSection.residencePermitStatus.values"
                            )
                          ),
                        })}
                      </Layout.Item>
                      <Layout.Item default="6/12" m="1/2" xs="1/1">
                        {renderRadioGroup({
                          fieldName: "workPermitStatus",
                          tooltip: undefined,
                          legend: Output.resolve(
                            "pages.heyCar.fullAppForm.contactSection.workPermitStatus.label"
                          ),
                          options: select(
                            ["restricted", "unrestricted"],
                            Output.resolve(
                              "pages.heyCar.fullAppForm.contactSection.workPermitStatus.values"
                            )
                          ),
                        })}
                      </Layout.Item>
                    </Layout>
                  </Fieldset.Row>
                  <FieldDependent
                    names={["workPermitStatus", "residencePermitStatus"]}
                    render={({
                      values: { workPermitStatus, residencePermitStatus },
                      formik,
                    }: {
                      values: any;
                      formik: any;
                    }) => (
                      <Conditional
                        collapsed={
                          workPermitStatus === "unrestricted" &&
                          residencePermitStatus === "unrestricted"
                        }
                        onCollapse={() => {
                          resetField("residencePermitEndDate", formik);
                          resetField("workPermitEndDate", formik);
                        }}
                      >
                        <Fieldset.Row>
                          <Layout>
                            <Layout.Item default="6/12" m="1/2" xs="1/1">
                              <FieldDependent
                                names={["residencePermitStatus"]}
                                render={({
                                  values: { residencePermitStatus },
                                  formik,
                                }: {
                                  values: any;
                                  formik: any;
                                }) => (
                                  <Conditional
                                    collapsed={
                                      residencePermitStatus === "unrestricted"
                                    }
                                    onCollapse={() => {
                                      resetField(
                                        "residencePermitEndDate",
                                        formik
                                      );
                                    }}
                                  >
                                    <FastField
                                      name="residencePermitEndDate"
                                      label={Output.resolve(
                                        "pages.heyCar.fullAppForm.contactSection.residencePermitEndDate"
                                      )}
                                      component={DateField}
                                    />
                                  </Conditional>
                                )}
                              />
                            </Layout.Item>
                            <Layout.Item default="6/12" m="1/2" xs="1/1">
                              <FieldDependent
                                names={["workPermitStatus"]}
                                render={({
                                  values: { workPermitStatus },
                                  formik,
                                }: {
                                  values: any;
                                  formik: any;
                                }) => (
                                  <Conditional
                                    collapsed={
                                      workPermitStatus === "unrestricted"
                                    }
                                    onCollapse={() => {
                                      resetField("workPermitEndDate", formik);
                                    }}
                                  >
                                    <FastField
                                      name="workPermitEndDate"
                                      label={Output.resolve(
                                        "pages.heyCar.fullAppForm.contactSection.workPermitEndDate"
                                      )}
                                      component={DateField}
                                    />
                                  </Conditional>
                                )}
                              />
                            </Layout.Item>
                          </Layout>
                        </Fieldset.Row>
                      </Conditional>
                    )}
                  />
                </Fieldset>
              </Conditional>
            )}
          />
          <Fieldset.Row>
            <Layout>
              <Layout.Item default={"6/12"} m={"1/2"} xs={"1/1"}>
                <FastField
                  name={"maritalStatus"}
                  label={Output.resolve(
                    "pages.applicationForm.panels.contactData.fields.maritalStatus.label"
                  )}
                  component={SelectField}
                >
                  {toOptions(
                    Output.resolve(
                      "pages.applicationForm.panels.contactData.fields.maritalStatus.values",
                      []
                    )
                  )}
                </FastField>
              </Layout.Item>
              <Layout.Item default="6/12" m="1/2" xs="1/1">
                {renderRadioGroup({
                  fieldName: "livingStatus",
                  tooltip: Output.resolveAsMarkdown(
                    "pages.applicationForm.panels.contactData.fields.livingTogether.toolTip"
                  ),
                  legend: Output.resolve(
                    "pages.applicationForm.panels.contactData.fields.livingTogether.label"
                  ),
                  options: select(
                    ["yes", "no"],
                    Output.resolve(
                      "pages.applicationForm.panels.contactData.fields.livingTogether.values"
                    )
                  ),
                })}
              </Layout.Item>
            </Layout>
          </Fieldset.Row>
          <Fieldset.Row>
            <Layout>
              <Layout.Item default="6/12" m="1/2" xs="1/1">
                <Field
                  name="email"
                  label={Output.resolve(
                    "pages.applicationForm.panels.contactData.fields.mail.label"
                  )}
                  tooltip={Output.resolveAsMarkdown(
                    "pages.applicationForm.panels.contactData.fields.mail.toolTip"
                  )}
                  component={TextField}
                />
              </Layout.Item>
            </Layout>
          </Fieldset.Row>
          <Fieldset.Row>
            <Layout>
              <Layout.Item default="6/12" m="1/2" xs="1/1">
                <FastField
                  name="mobilePhoneCountryCode"
                  label={Output.resolve(
                    "pages.applicationForm.panels.contactData.fields.telephonePrefix.label"
                  )}
                  component={SelectField}
                >
                  {Output.resolve(
                    "pages.applicationForm.panels.contactData.fields.telephonePrefix.values",
                    []
                  ).map(
                    (
                      {
                        key: value,
                        value: label,
                      }: { key: string; value: string },
                      index: number
                    ) => (
                      <option key={index} value={value}>
                        {label}
                      </option>
                    )
                  )}
                </FastField>
              </Layout.Item>
              <Layout.Item default="6/12" m="1/2" xs="1/1">
                <FastField
                  name="mobilePhoneNumber"
                  label={Output.resolve(
                    "pages.applicationForm.panels.contactData.fields.telephone.label"
                  )}
                  tooltip={Output.resolveAsMarkdown(
                    "pages.applicationForm.panels.contactData.fields.telephone.toolTip"
                  )}
                  component={TextField}
                />
              </Layout.Item>
            </Layout>
          </Fieldset.Row>
        </Fieldset>
        <Fieldset>
          <Fieldset.Row>{buttons}</Fieldset.Row>
        </Fieldset>
      </Form>
    </FormSection2>
  );
};

ContactDetailsSectionFull.defaultProps = {
  validate: Validation.select(
    "street",
    "housenumber",
    "postcode",
    "city",
    "country",
    "residentSince",
    "prevstreet",
    "prevhousenumber",
    "prevpostcode",
    "prevcity",
    "prevcountry",
    "isLivingInDeStartDate",
    "residencePermitStatus",
    "workPermitStatus",
    "residencePermitEndDate",
    "workPermitEndDate",
    "maritalStatus",
    "livingStatus",
    "email",
    "mobilePhoneCountryCode",
    "mobilePhoneNumber"
  ),
};

export { ContactDetailsSectionFull };
